import { TILE_CONFIG } from "@/config";
import { loadedAssets } from "@/loader";
import Location from "@/scenes/location";
import { createTintedMaterial } from "@/utils/materials";
import { tileToWorld, worldToTile } from "@/utils/tile";
import { AsepriteResource } from "@excaliburjs/plugin-aseprite";
import { Actor, Animation, AnimationStrategy, Color, vec } from "excalibur";
import gameApp from "..";

export class Furniture extends Actor {
  private animationStates: number[] = [1, 2, 4, 6];
  private currentIndex: number = 0;
  private isDragging: boolean = false; // Track drag state
  private isPlaying: boolean = false; // Track drag state
  private initialPointerOffset: any = null; // To maintain offset when dragging
  animation: any;
  stackLevel: number;
  x: number;
  y: number;
  currentWidth: number;
  canSit: any;
  slug: string;
  scene: Location;
  direction: number;
  isFake: boolean = false;
  furnitureId: number;
  clickTimeout: any;
  spriteResource: any;

  constructor({
    id,
    x,
    y,
    slug,
    canSit,
    direction = 0,
    stackLevel = 0,
    isPlaying = false,
  }: {
    id: number;
    x: any;
    y: any;
    slug: string;
    canSit: boolean;
    direction?: number;
    stackLevel?: number;
    isPlaying: boolean;
  }) {
    super();

    this.furnitureId = id;
    this.x = x;
    this.y = y;
    this.stackLevel = stackLevel;
    this.slug = slug;
    this.canSit = canSit;
    this.direction = direction;
    this.isPlaying = isPlaying;
  }

  public onInitialize() {
    if (this.x && this.y) {
      const tileCenter = tileToWorld(vec(this.x, this.y));
      this.pos = this.scene.roomTileMap.tileMap.pos.add(tileCenter);
    }

    if (this.direction) {
      this.currentIndex = this.animationStates.indexOf(this.direction);
    }

    this.updateAnimation();
    this.setEventListeners();
  }

  private updateAnimation(): void {
    if (!this.slug) return console.warn("Slug is not added to the furniture");

    const currentState = this.animationStates[this.currentIndex].toString();
    const resource = loadedAssets[this.slug] as AsepriteResource;
    if (!resource) return;

    if (!resource?.data?.animations?.has(currentState)) {
      this.currentIndex = 0;
      this.updateAnimation();
      return;
    }

    const spriteResource = resource.clone().getAnimation(currentState);
    const animationFrames: any[][] = [];

    const baseFrame = spriteResource?.frames?.[0]?.graphic?.clone() as any;
    if (!baseFrame) return;

    const totalFrames = spriteResource?.frames?.length || 0;
    const totalParts = Math.ceil(
      baseFrame.sourceView.width / TILE_CONFIG.TILE_WIDTH
    );
    const partWidth = baseFrame.sourceView.width / totalParts;

    for (let partIndex = 0; partIndex < totalParts; partIndex++) {
      animationFrames[partIndex] = [];

      for (let frameIndex = 0; frameIndex < totalFrames; frameIndex++) {
        const frameGraphic = spriteResource?.frames?.[
          frameIndex
        ]?.graphic?.clone() as any;
        if (!frameGraphic) continue;

        const partGraphic = frameGraphic.clone();
        partGraphic.width = partWidth;
        partGraphic.sourceView.width = partWidth;
        partGraphic.sourceView.x += partIndex * partWidth;

        animationFrames[partIndex].push({
          graphic: partGraphic,
          duration: spriteResource?.frames?.[0]?.duration,
        });
      }
    }

    animationFrames.forEach((frames, partIndex) => {
      const animation = new Animation({ frames });
      if (this.isPlaying) {
        animation.strategy = AnimationStrategy.Freeze;
        animation.play();
      } else {
        animation.pause();
        animation.goToFrame(0);
      }

      const actor = new Actor({
        pos: vec(partIndex * partWidth, 0),
        anchor: vec(0, 1),
        z: this.y + partIndex,
        offset: vec(-25, 25),
      });

      actor.graphics.use(animation);
      this.addChild(actor);
    });

    this.spriteResource = spriteResource;
  }

  public toggleAnimationState(state: boolean) {
    this.isPlaying = state;
    this.removeAllChildren();
    this.updateAnimation();
  }

  public async rotate(): Promise<void> {
    this.currentIndex = (this.currentIndex + 1) % this.animationStates.length;

    this.removeAllChildren();
    this.updateAnimation();
  }

  public remove(): void {
    this.kill();
  }

  public setFake() {
    this.isFake = true;
  }

  public move(x: number, y: number, scene?: any): void {
    const currentScene = scene || this.scene;
    const currentPostion = vec(x, y);

    this.initialPointerOffset = currentPostion.sub(currentPostion);
    const mousePosition =
      currentScene.engine.input.pointers.primary.lastWorldPos;
    this.pos = mousePosition.sub(this.initialPointerOffset);

    this.isDragging = true;
    currentScene.roomTileMap.activePlayerStopped = true;
  }

  private snapToTile(): void {
    const tilemap = this.scene.roomTileMap.tileMap.pos;
    const tile = this.getTilePosition();
    this.pos = tileToWorld(tile).add(tilemap);
  }

  public setPosition(scene, x, y) {
    const tilemap = scene.roomTileMap.tileMap.pos;
    this.pos = tileToWorld(vec(x, y)).add(tilemap);

    this.x = x;
    this.y = y;
  }

  private getTilePosition() {
    const tilemap = this.scene.roomTileMap.tileMap.pos;
    return worldToTile(this.pos.sub(tilemap));
  }

  public setEventListeners(): void {
    this.scene.engine.input.pointers.primary.on("move", (evt) => {
      if (this.isDragging) {
        this.pos = evt.worldPos.sub(this.initialPointerOffset);

        const tilePosition = this.getTilePosition();
        const tile = this.scene.roomTileMap.tileMap.getTile(
          tilePosition.x,
          tilePosition.y
        );

        // Set opacity based on whether the tile exists and is not solid
        this.graphics.opacity = tile ? 1 : 0.5;

        this.graphics.material =
          tile && tile.solid
            ? createTintedMaterial(this.scene.engine, Color.Red)
            : null;

        // Adjust z index if the tile exists
        if (tile) {
          // this.z = tile.x + tile.y + 1;
        }
      }
    });

    if (!this.isFake) {
      this.children.forEach((child) => {
        child.events.on("pointerdown", (e) => {
          if (this.clickTimeout) {
            // Double-click detected
            clearTimeout(this.clickTimeout);
            this.clickTimeout = null;
            gameApp.wsClient.sendMessage(
              JSON.stringify({
                type: "furniture-actions",
                arguments: {
                  action: "set_state",
                  id: this.furnitureId,
                },
              })
            );
          } else {
            // Single click logic, start a timeout
            this.clickTimeout = setTimeout(async () => {
              this.clickTimeout = null;

              // Single-click logic here
              this.scene.userViewer?.closeView();

              await this.scene.furniViewer?.modify({
                id: this.furnitureId,
                name: this.slug,
                furnitureResource: this.spriteResource,
              });

              this.scene.furniViewer?.openView();
            }, 300); // Timeout for distinguishing double-click, typically 300ms
          }
        });
      });
    }

    this.scene.engine.input.pointers.primary.on("up", (evt) => {
      if (this.isDragging) {
        if (this.isFake) {
          this.kill();
        }
        const tilePosition = this.getTilePosition();

        const tile = this.scene.roomTileMap.tileMap.getTile(
          tilePosition.x,
          tilePosition.y
        );

        if (!tile || (tile && tile.solid)) return;

        // Check if tile exists, if not disable placing furniture
        this.isDragging = false;
        this.snapToTile();

        const event = new CustomEvent("furni-dropped", {
          detail: {
            id: this.furnitureId,
            slug: this.slug,
            originalPos: { x: this.x, y: this.y },
            newPos: this.pos,
            snappedTile: tilePosition,
            isNew: this.isFake,
          },
        });

        window.dispatchEvent(event);

        // Reactive the player being able to walk again
        this.scene.roomTileMap.activePlayerStopped = false;
      }
    });
  }
}
